import { ToggleGroup, ToggleGroupItem } from '@eventbrite/marmalade';
import { useRef } from 'react';
import { useGlobalNavContext } from '../../../../state';
import { IntentToggleOptions, type AttendeeCreatorToggleProps } from '../../../../types';
import styles from './AttendeeCreatorToggle.module.scss';
import { trackClickWithDimensions } from '../../../../utils/tracking';
import { navigateTo } from '../../../../utils/navigation';
import { useIntl } from 'react-intl';
interface IntentClickDimensions extends Record<string, string> {
    hostEventsRedirectPath: string;
    activeOption: string;
    clickedOption: string;
}

const getFindEventsHref = (serverUrl?: string) => {
    if (!serverUrl) return DEFAULT_HREF_PATHS.HOMEPAGE;
    const currentUrl = new URL(serverUrl);
    const url = new URL(DEFAULT_HREF_PATHS.HOMEPAGE, currentUrl.origin);
    url.search = currentUrl.search;
    return url.href;
};

const getHostEventsHref = (serverUrl?: string, hostEventsRedirectURL?: string) => {
    const path = hostEventsRedirectURL ?? DEFAULT_HREF_PATHS.ORGANIZER_OVERVIEW;
    if (!serverUrl) return path;
    const currentUrl = new URL(serverUrl);
    const url = new URL(path, currentUrl.origin);
    url.search = currentUrl.search;
    return url.href;
};

const METRIC_NAME_CLICK = 'GlobalNav - Logged out - Click - Intent Toggle';

const DEFAULT_HREF_PATHS = {
    ORGANIZER_OVERVIEW: '/organizer/overview',
    HOMEPAGE: '/',
} as const;

const getPathDimension = (urlOrPath: string): string => {
    try {
        const fullURL = new URL(urlOrPath);
        return fullURL.pathname;
    } catch (TypeError) {
        return urlOrPath;
    }
};

export const AttendeeCreatorToggle = ({
    activeOption = IntentToggleOptions.FIND_EVENTS,
    hostEventsRedirectURL,
}: AttendeeCreatorToggleProps) => {
    const { env } = useGlobalNavContext();
    const lastHandledValueRef = useRef<IntentToggleOptions | null>(null);
    const findEventsHref = getFindEventsHref(env?.serverUrl);
    const hostEventsHref = getHostEventsHref(env?.serverUrl, hostEventsRedirectURL);
    const { formatMessage } = useIntl();
    const redirectAndTrack = (newValue: string) => {
        // This is "hack" to fix an issue in the Marmalade ToggleGroup
        // component. See: https://github.com/eventbrite/marmalade/issues/459
        //
        // In nutshell, the `onValueChange` property gets called twice. This
        // duplicates tracking of clicks which is undesired.
        // The workaround is to keep a status in the AttendeeCreatorToggle
        // component itself. This way we stora the "last active option", then
        // we can de-duplicate them and only track metrics or redirect when it
        // actually changed.
        //
        // This a best effort until the UI component gets fixed.

        const newOption = newValue as IntentToggleOptions;
        if (newOption === lastHandledValueRef.current) return;

        const previousOption = lastHandledValueRef.current;
        lastHandledValueRef.current = newOption;

        const targetHref =
            newOption === IntentToggleOptions.FIND_EVENTS ? findEventsHref : hostEventsHref;

        // The initial value of the activeOption is set to null. This is to
        // prevent "missing clicks" when the current "activeOption" is clicked.
        // This works for the first time it's clicked.
        const getFirstStateFallback = () => {
            return previousOption === null ? activeOption : previousOption;
        };
        const dimensions: IntentClickDimensions = {
            hostEventsRedirectPath: getPathDimension(targetHref),
            activeOption: getFirstStateFallback(),
            clickedOption: newOption.toString(),
        };
        trackClickWithDimensions(METRIC_NAME_CLICK, dimensions);

        navigateTo(targetHref);
    };

    return (
        <div className={styles.toggleContainer}>
            <ToggleGroup
                aria-label={formatMessage({
                    id: 'Choose between finding or hosting events',
                    defaultMessage: 'Choose between finding or hosting events',
                })}
                defaultValue={activeOption}
                onValueChange={redirectAndTrack}
                isDeselectable={false}
                placeholder={formatMessage({
                    id: 'Choose an option',
                    defaultMessage: 'Choose an option',
                })}
            >
                <ToggleGroupItem
                    value={IntentToggleOptions.FIND_EVENTS}
                    aria-label={formatMessage({ id: 'Find events', defaultMessage: 'Find events' })}
                >
                    {formatMessage({ id: 'Find events', defaultMessage: 'Find events' })}
                </ToggleGroupItem>
                <ToggleGroupItem
                    value={IntentToggleOptions.HOST_EVENTS}
                    aria-label={formatMessage({ id: 'Host events', defaultMessage: 'Host events' })}
                >
                    {formatMessage({ id: 'Host events', defaultMessage: 'Host events' })}
                </ToggleGroupItem>
            </ToggleGroup>
        </div>
    );
};
